import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { t, STRINGS } from "@/lib/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SMS_CONSENT_VERSION } from "@/lib/consent";
import SmsConsent from "@/components/gentry/SmsConsent";

function fmtDate(d) {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function AppointmentPicker({ open, onOpenChange, lang, sessionId }) {
  const nextAvailableBusinessDay = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    // Move forward until we hit Mon-Sat (skip Sunday = 0)
    while (d.getDay() === 0) d.setDate(d.getDate() + 1);
    return d;
  };

  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [time, setTime] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setDate((prev) => prev || nextAvailableBusinessDay());
    } else {
      setDate(null);
      setSlots([]);
      setTime(null);
      setName("");
      setPhone("");
      setEmail("");
      setReason("");
      setSmsConsent(false);
    }
  }, [open]);

  useEffect(() => {
    const iso = fmtDate(date);
    if (!iso) {
      setSlots([]);
      setTime(null);
      return;
    }
    let cancelled = false;
    setLoadingSlots(true);
    api
      .get(`/appointments/slots`, { params: { date: iso } })
      .then(({ data }) => {
        if (cancelled) return;
        setSlots(data.slots || []);
        setTime(null);
      })
      .catch(() => !cancelled && setSlots([]))
      .finally(() => !cancelled && setLoadingSlots(false));
    return () => {
      cancelled = true;
    };
  }, [date]);

  const submit = async () => {
    if (!date || !time || !name.trim() || !phone.trim()) {
      toast.error(lang === "ht" ? "Tanpri ranpli tout chan yo." : "Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/appointments", {
        session_id: sessionId,
        name,
        phone,
        email: email || null,
        reason: reason || (lang === "ht" ? "Randevou jeneral" : "General appointment"),
        date: fmtDate(date),
        time,
        language: lang,
        sms_consent: smsConsent,
        sms_consent_text: smsConsent ? STRINGS[lang].smsConsentBody : null,
        sms_consent_version: smsConsent ? SMS_CONSENT_VERSION : null,
      });
      toast.success(t(lang, "successAppointment"));
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const isPastDay = (d) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-2xl" data-testid="appointment-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#1E3B2F]">
            {t(lang, "scheduleTitle")}
          </DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="font-label text-[10px] text-[#6B7280] mb-2">
              {t(lang, "pickDate")}
            </div>
            <div className="rounded-lg border border-[#EBE4D3] bg-[#F9F6F0] p-2 inline-block">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={isPastDay}
                data-testid="appointment-calendar"
              />
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <div className="font-label text-[10px] text-[#6B7280] mb-2">
                {t(lang, "availableSlots")}
              </div>
              {!date ? (
                <p className="text-sm text-[#6B7280]">
                  {lang === "ht" ? "Chwazi yon dat premye." : "Pick a date first."}
                </p>
              ) : loadingSlots ? (
                <p className="text-sm text-[#6B7280]">…</p>
              ) : slots.length === 0 ? (
                <p className="text-sm text-[#6B7280]">{t(lang, "noSlots")}</p>
              ) : (
                <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                  {slots.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setTime(s)}
                      data-testid={`appointment-slot-${s}`}
                      className={
                        "h-9 rounded-md text-xs font-medium border transition-colors " +
                        (time === s
                          ? "bg-[#C25934] text-white border-[#C25934]"
                          : "bg-white text-[#1F1F1F] border-[#EBE4D3] hover:border-[#C25934]")
                      }
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="font-label text-[10px] text-[#6B7280]">
                {t(lang, "yourName")}
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-11 bg-[#F9F6F0] border-[#EBE4D3]"
                data-testid="appointment-name-input"
              />
            </div>
            <div className="space-y-2">
              <label className="font-label text-[10px] text-[#6B7280]">{t(lang, "phone")}</label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="h-11 bg-[#F9F6F0] border-[#EBE4D3]"
                data-testid="appointment-phone-input"
                type="tel"
              />
            </div>
            <div className="space-y-2">
              <label className="font-label text-[10px] text-[#6B7280]">{t(lang, "email")}</label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 bg-[#F9F6F0] border-[#EBE4D3]"
                data-testid="appointment-email-input"
                placeholder={t(lang, "emailPlaceholder")}
                type="email"
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <label className="font-label text-[10px] text-[#6B7280]">{t(lang, "reason")}</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="min-h-16 bg-[#F9F6F0] border-[#EBE4D3]"
                data-testid="appointment-reason-input"
              />
            </div>

            <SmsConsent checked={smsConsent} onCheckedChange={setSmsConsent} lang={lang} />

            <Button
              onClick={submit}
              disabled={submitting || !date || !time}
              className="w-full h-11 bg-[#C25934] hover:bg-[#a44827] text-white btn-lift"
              data-testid="appointment-submit-button"
            >
              {smsConsent ? t(lang, "submitBtnSignUp") : t(lang, "submitBtnBook")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
